import { useState, useRef, useEffect } from "react";
import BandSelectionPanel from "@/components/BandSelectionPanel";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError, downloadBlob, retryRequest } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Map as MapIcon, Loader2, Download, Upload } from "lucide-react";
import GeoPiButton from "@/components/GeoPiButton";
import { GeoTiffLayer } from "@/components/GeoTiffLayer";

const MAPS = [
  ["location", "Project Location"], ["satellite", "World / Satellite Imagery"], ["dem", "Digital Elevation Model"],
  ["hillshade", "Hillshade / Relief"], ["slope", "Slope"], ["aspect", "Aspect"],
  ["contour", "Contour"], ["drainage", "Stream / Drainage"], ["topography", "Topography"],
];

export default function MapGenWorkspace() {
  const nav = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [area, setArea] = useState(null);
  const [sel, setSel] = useState(MAPS.map((m) => m[0]));
  const [busy, setBusy] = useState("");
  const [maps, setMaps] = useState({});
  const [job, setJob] = useState(null);
  const poll = useRef(null);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => {
      setProject(r.data);
      setArea(r.data.boundary_metrics?.area_km2 ?? null);
    }).catch(() => {});
    api.get(`/mapgen/${id}/maps`).then(({ data }) => {
      if (data.job?.status === "Completed" && data.maps) setMaps(data.maps);
    }).catch(() => {});
    return () => { if (poll.current) clearInterval(poll.current); };
  }, [id]);

  const uploadBoundary = async (file) => {
    if (!file) return;
    setBusy("boundary");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await retryRequest(() => api.post(`/projects/${id}/boundary/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }), { retries: 1, delay: 2000 });
      setArea(data.metrics?.area_km2 ?? data.boundary_metrics?.area_km2 ?? null);
      toast.success("Boundary validated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const generate = async () => {
    if (!sel.length) return toast.error("Select at least one map");
    setBusy("gen"); setMaps({});
    try {
      await api.post(`/mapgen/${id}/generate`, { maps: sel });
      toast.info("Generating maps - streaming DEM & terrain…");
      poll.current = setInterval(async () => {
        const { data } = await api.get(`/mapgen/${id}/maps`);
        setJob(data.job);
        if (data.job?.status === "Completed") { clearInterval(poll.current); setMaps(data.maps || {}); setBusy(""); toast.success("Maps ready"); }
        if (data.job?.status === "Failed") { clearInterval(poll.current); setBusy(""); toast.error(data.job?.error || "Generation failed"); }
      }, 3000);
    } catch (e) { setBusy(""); toast.error(apiError(e)); }
  };

  const label = (k) => (MAPS.find((m) => m[0] === k) || [k, k])[1];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="min-h-16 bg-white border-b border-slate-200 flex items-center flex-wrap gap-x-4 gap-y-1 px-4 sm:px-6 py-2">
        <button onClick={() => nav("/console")} data-testid="back-to-modes" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={16} /> Back to Console</button>
        <button onClick={() => nav("/maps")} data-testid="mapgen-back" className="text-sm text-slate-500 hover:text-slate-900 border-l border-slate-200 pl-4">Projects</button>
        <div className="hidden sm:block h-5 w-px bg-slate-200" />
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 min-w-0"><MapIcon size={16} className="text-orange-500 shrink-0" /> <span className="truncate">SYMIN &gt; {project?.name || "Geomapping"}</span></div>
        <div className="ml-auto"><GeoPiButton compact project={{ id: project?.id || id, name: project?.name || "Geomapping project", context: `Module: Geomapping (high-resolution geological & GIS mapping). Project: ${project?.name || ""}.` }} /></div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6" data-testid="mapgen-step-boundary">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-4">01 - Boundary / Data</div>
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 h-10 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:border-orange-400">
            <input type="file" accept=".kml,.kmz,.geojson,.json" className="hidden" data-testid="mapgen-boundary-input" onChange={(e) => uploadBoundary(e.target.files?.[0])} />
            {busy === "boundary" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload KML / KMZ / GeoJSON
          </label>
          {area != null && <div className="mt-3 text-sm text-slate-700" data-testid="mapgen-area">Validated area: <b>{Number(area).toFixed(2)} km²</b></div>}
        </section>

        <GeoTiffLayer pid={id} boundary={project?.boundary} />

        {project && <BandSelectionPanel projectId={id} commodity={project?.commodity} />}

        {area != null && (
          <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6" data-testid="mapgen-step-select">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-4">02 - Select Maps</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
              {MAPS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" data-testid={`mapgen-sel-${k}`} checked={sel.includes(k)} onChange={() => toggle(k)} className="h-4 w-4 accent-orange-500" />{l}
                </label>
              ))}
            </div>
            <Button data-testid="mapgen-generate" onClick={generate} disabled={busy === "gen"} className="mt-5 bg-orange-500 hover:bg-orange-600 text-white h-10 px-6 w-full sm:w-auto">
              {busy === "gen" ? <><Loader2 size={15} className="mr-2 animate-spin" />{job?.stage || "Working…"}</> : "Generate & Download"}
            </Button>
          </section>
        )}

        {Object.keys(maps).length > 0 && (
          <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6" data-testid="mapgen-results">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">03 - Generated Maps</div>
              <Button data-testid="mapgen-download-all" onClick={() => downloadBlob(`/mapgen/${id}/download-all`, `SYMIN_maps.zip`)} className="bg-slate-900 hover:bg-slate-800 text-white h-9"><Download size={14} className="mr-2" />Download All</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(maps).map(([k, uri]) => (
                <div key={k} className="border border-slate-200 rounded-lg overflow-hidden">
                  <img src={uri} alt={k} className="w-full object-contain bg-slate-50" data-testid={`mapgen-img-${k}`} />
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm font-medium text-slate-700">{label(k)}</span>
                    <button data-testid={`mapgen-dl-${k}`} onClick={() => downloadBlob(`/mapgen/${id}/map/${k}`, `${k}.png`)} className="text-slate-400 hover:text-orange-600"><Download size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
